import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../AuthContext.jsx'
import GrowLabLogo from '../components/GrowLabLogo.jsx'
import {
  getResellerDashboard,
  generateInviteLink,
  addSeats,
  suspendResellerUser,
  restoreResellerUser,
  removeResellerUser,
} from '../api.js'

export default function Reseller() {
  const { user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (user && user.role !== 'reseller') {
      navigate('/dashboard', { replace: true })
    }
  }, [user])

  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [demoCopied, setDemoCopied] = useState(false)
  const [inviteUrl, setInviteUrl] = useState(null)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteCopied, setInviteCopied] = useState(false)
  const [seatsQuantity, setSeatsQuantity] = useState(1)
  const [seatsLoading, setSeatsLoading] = useState(false)
  const [seatsError, setSeatsError] = useState('')
  const [confirmRemove, setConfirmRemove] = useState(null)
  const [actionLoading, setActionLoading] = useState(null)

  useEffect(() => { fetchDashboard() }, [])

  async function fetchDashboard() {
    setLoading(true)
    setError('')
    try {
      const { data } = await getResellerDashboard()
      setDashboard(data)
    } catch {
      setError('Failed to load reseller dashboard.')
    } finally {
      setLoading(false)
    }
  }

  function copyDemo() {
    navigator.clipboard.writeText(dashboard.demo_link).then(() => {
      setDemoCopied(true)
      setTimeout(() => setDemoCopied(false), 2000)
    })
  }

  async function handleGenerateInvite() {
    if (dashboard?.seats_available === 0) return
    setInviteLoading(true)
    try {
      const { data } = await generateInviteLink()
      setInviteUrl(data.invite_url)
    } catch {
      setError('Failed to generate invite link.')
    } finally {
      setInviteLoading(false)
    }
  }

  function copyInvite() {
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setInviteCopied(true)
      setTimeout(() => setInviteCopied(false), 2000)
    })
  }

  async function handleBuySeats() {
    if (!seatsQuantity || seatsQuantity < 1) return
    setSeatsLoading(true)
    setSeatsError('')
    try {
      await addSeats(seatsQuantity)
      await fetchDashboard()
    } catch {
      setSeatsError('Failed to add seats. Please try again.')
    } finally {
      setSeatsLoading(false)
    }
  }

  async function handleSuspend(userId) {
    setActionLoading(userId)
    try {
      await suspendResellerUser(userId)
      await fetchDashboard()
    } catch {}
    finally { setActionLoading(null) }
  }

  async function handleRestore(userId) {
    setActionLoading(userId)
    try {
      await restoreResellerUser(userId)
      await fetchDashboard()
    } catch {}
    finally { setActionLoading(null) }
  }

  async function handleRemove(userId) {
    setActionLoading(userId)
    setConfirmRemove(null)
    try {
      await removeResellerUser(userId)
      await fetchDashboard()
    } catch {}
    finally { setActionLoading(null) }
  }

  if (loading) return <div className="page-loading">Loading…</div>

  const seatPct = dashboard
    ? Math.min(100, Math.round((dashboard.seats_used / dashboard.seat_limit) * 100))
    : 0

  return (
    <div className="dash-page">
      {/* Topbar */}
      <div className="dash-topbar">
        <Link to="/dashboard" className="dash-back-link">← Dashboard</Link>
        <div className="dash-topbar-logo">
          <GrowLabLogo size={20} />
          <span>GROWLAB CLOUD</span>
        </div>
      </div>

      {/* Title row */}
      <div className="dash-header">
        <div className="dash-title">Reseller Dashboard</div>
        {dashboard && (
          <span className="seats-badge">
            {dashboard.seats_used} / {dashboard.seat_limit} seats
          </span>
        )}
      </div>

      {/* Inactive warning banner */}
      {dashboard?.status !== 'active' && (
        <div className="reseller-inactive-banner">
          Your reseller subscription is not active. Users cannot access GrowLab.
        </div>
      )}

      {error && <p className="dash-error">{error}</p>}

      {/* Invite URL modal */}
      {inviteUrl && (
        <div
          className="paywall-backdrop"
          onClick={() => { setInviteUrl(null); setInviteCopied(false) }}
          aria-hidden="true"
        >
          <div
            className="paywall-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              className="paywall-close"
              onClick={() => { setInviteUrl(null); setInviteCopied(false) }}
              aria-label="Close"
            >✕</button>
            <div className="card-label" style={{ marginBottom: 12 }}>Activation Link</div>
            <div className="reseller-input-row">
              <input
                readOnly
                value={inviteUrl}
                className="reseller-input"
              />
              <button
                className="dash-btn-outline reseller-copy-btn"
                onClick={copyInvite}
              >
                {inviteCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <button
              className="paywall-subscribe-btn"
              onClick={() => { setInviteUrl(null); setInviteCopied(false) }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {dashboard && (
        <div className="reseller-grid">
          {/* Left main column */}
          <div className="reseller-main">
            {/* Demo Link card */}
            <div className="dash-card">
              <div className="card-label">Demo Link — share with potential customers</div>
              <div className="reseller-input-row">
                <input
                  readOnly
                  value={dashboard.demo_link}
                  className="reseller-input"
                />
                <button
                  className="dash-btn-outline reseller-copy-btn"
                  onClick={copyDemo}
                >
                  {demoCopied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
              <div className="reseller-hint">
                Customers who register via this link get free trial tokens. Send them an activation link after closing the deal.
              </div>
            </div>

            {/* Provisioned Users card */}
            <div className="dash-card">
              <div className="card-label">Provisioned Users</div>
              {!dashboard.provisioned_users?.length ? (
                <div className="reseller-empty">
                  No users yet. Share your demo link or generate an activation link to get started.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="reseller-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Joined</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboard.provisioned_users.map((u) => {
                        const badge = u.is_reseller_demo
                          ? { label: 'Demo', cls: 'badge-demo' }
                          : u.is_active
                          ? { label: 'Active', cls: 'badge-active' }
                          : { label: 'Suspended', cls: 'badge-suspended' }

                        return (
                          <tr key={u.id}>
                            <td>{u.email}</td>
                            <td>{u.name || '—'}</td>
                            <td>
                              <span className={`reseller-badge ${badge.cls}`}>
                                {badge.label}
                              </span>
                            </td>
                            <td className="reseller-td-muted">
                              {u.created_at
                                ? new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                : '—'}
                            </td>
                            <td>
                              <div className="reseller-actions">
                                {u.is_active && !u.is_reseller_demo && (
                                  <button
                                    className="dash-btn-outline reseller-action-btn"
                                    onClick={() => handleSuspend(u.id)}
                                    disabled={actionLoading === u.id}
                                  >
                                    {actionLoading === u.id ? '…' : 'Suspend'}
                                  </button>
                                )}
                                {!u.is_active && (
                                  <button
                                    className="dash-btn-outline reseller-action-btn"
                                    onClick={() => handleRestore(u.id)}
                                    disabled={actionLoading === u.id}
                                  >
                                    {actionLoading === u.id ? '…' : 'Restore'}
                                  </button>
                                )}
                                {confirmRemove === u.id ? (
                                  <>
                                    <span className="reseller-confirm-text">Are you sure?</span>
                                    <button
                                      className="reseller-remove-confirm-btn"
                                      onClick={() => handleRemove(u.id)}
                                      disabled={actionLoading === u.id}
                                    >
                                      {actionLoading === u.id ? '…' : 'Yes'}
                                    </button>
                                    <button
                                      className="dash-btn-outline reseller-action-btn"
                                      onClick={() => setConfirmRemove(null)}
                                    >
                                      No
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    className="reseller-remove-btn"
                                    onClick={() => setConfirmRemove(u.id)}
                                    disabled={actionLoading === u.id}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Right sidebar */}
          <div className="reseller-sidebar">
            {/* Activation Link card */}
            <div className="dash-card">
              <div className="card-label">Activation Link</div>
              {dashboard.seats_available === 0 && (
                <div className="reseller-hint" style={{ marginBottom: 10 }}>
                  No seats available — buy more seats below
                </div>
              )}
              <button
                className="dash-btn"
                onClick={handleGenerateInvite}
                disabled={dashboard.seats_available === 0 || inviteLoading}
              >
                {inviteLoading ? 'Generating…' : 'Generate Activation Link'}
              </button>
            </div>

            {/* Buy More Seats card */}
            <div className="dash-card">
              <div className="card-label">Buy More Seats</div>
              <div className="reseller-seats-row">
                <input
                  type="number"
                  min="1"
                  value={seatsQuantity}
                  onChange={(e) => setSeatsQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="reseller-seats-input"
                />
                <button
                  className="dash-btn-outline"
                  style={{ flex: 1 }}
                  onClick={handleBuySeats}
                  disabled={seatsLoading}
                >
                  {seatsLoading ? 'Processing…' : 'Buy Seats'}
                </button>
              </div>
              {seatsError && (
                <div style={{ color: 'var(--danger)', fontSize: '0.8rem', marginTop: 8 }}>
                  {seatsError}
                </div>
              )}
            </div>

            {/* Seat Usage card */}
            <div className="dash-card">
              <div className="card-label">Seat Usage</div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${seatPct}%` }} />
              </div>
              <div className="usage-text" style={{ marginTop: 6 }}>
                {dashboard.seats_used} of {dashboard.seat_limit} seats filled · {dashboard.seats_available} remaining
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
